import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { Typography, Button, message, Modal, Spin, Input, Select, Checkbox, DatePicker, Space, Switch } from 'antd';
import { LeftOutlined, PlusOutlined } from '@ant-design/icons';
import type { LaneCard, ProductTypeId, ProductTypeCondition, AccommodationCondition} from '../CommissionManager/types';
import { FieldLibrary } from '../CommissionManager/components/FieldLibrary';
import { ParameterSidebar } from '../CommissionManager/components/ParameterSidebar';
import { TaxRuleBlock } from './components/TaxRuleBlock';
import type { TaxBandRule } from './taxTypes';
import { getTaxRuleById } from 'app/shared/api/taxRules/getTaxRuleById';
import { checkTaxCodeIsUnique } from 'app/shared/api/taxRules/checkTaxCodeIsUnique';
import { saveTaxRule } from 'app/shared/api/taxRules/saveTaxRule';
import { buildTaxRulePutPayload } from 'app/shared/api/taxRules/serializeTaxRule';
import { taxRuleDtoToLoadedState } from 'app/shared/api/taxRules/parseTaxRuleDto';
import type { TaxProductNameLookup } from 'app/shared/api/taxRules/taxBandMappers';
import { loadTaxManagerProductCatalog } from 'app/shared/api/taxManager/loadTaxManagerProductCatalog';
import { getAllAccommodationTypes } from 'app/shared/api/getAllAccommodationTypes';
import { getAllAddonTypes } from 'app/shared/api/getAllAddonTypes';
import { getAllCountries, type CountryWithNumericId } from 'app/shared/api/getCountries';
import { getCentreById, getCentres, getCentresInDetails, isCentreListItemActive, positiveNumericCentreId, type CentreListItem} from 'app/shared/api/getCentres';
import type { Fee } from 'app/shared/model';
import type { MirrorSyncContext } from '../CommissionManager/mirrorSync';
import type { Dayjs } from 'dayjs';
import DateHelper from 'app/helpers/dateHelper';
import {
  buildMirrorSyncContext,
  centreFilterCountryKeys,
  centreOptionValue,
  isCountrySelectionEmpty,
  hasCentreSelectionForHeader,
  extractNumericCentreId,
  countryOptionValue,
  generateCardId,
  getSummary,
  formatTaxSaveErrorMessage,
  hasIncompleteProductSelection,
  hasTaxCountryScopeSet,
  mapIdToName,
  appendCentreSelectFallbackIfMissing,
  buildCentreOptionValueByAnyId,
  extractCentreScopeLabelFromTaxRuleDto,
  matchCentreSelectValue,
  matchCountrySelectValue,
  taxCentreIdForSavePayload,
  newBandFromTaxName,
  normIdNameList,
  normTypeList,
  optionsForProductType,
  resolveTaxRuleCountryForPayload,
  syncTaxBandMirrors,
  computeTaxCodeFieldError,
  isTaxCodeValidForSave,
  mergeTaxCodeInlineError,
  normalizeTaxCodeDigits,
  parseApiBandIdFromRuleId,
  type TaxCodeGlobalDupState,
  typeIdByProductMap,
} from './addTaxPageHelpers';

import '../CommissionManager/AddCommissionPage.css';
import './TaxManager.css';

const { Title } = Typography;

function parseTaxManagerRoute(pathname: string): { isAdd: boolean; editId: number | null } {
  const isAdd = pathname.endsWith('/add');
  const idMatch = pathname.match(/\/tax-manager\/(\d+)$/);
  return { isAdd, editId: idMatch ? Number(idMatch[1]) : null };
}

export const AddTaxPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdd, editId } = parseTaxManagerRoute(location.pathname);

  const [taxName, setTaxName] = useState('');
  const [specificCentre, setSpecificCentre] = useState(false);
  const [countryId, setCountryId] = useState<number | string | null>(null);
  const [centreId, setCentreId] = useState<number | string | null>(null);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [includeEndDate, setIncludeEndDate] = useState(false);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [taxIsActive, setTaxIsActive] = useState(true);
  const [bands, setBands] = useState<TaxBandRule[]>([]);
  const [rulesSectionVisible, setRulesSectionVisible] = useState(false);

  const [countries, setCountries] = useState<CountryWithNumericId[]>([]);
  const [centres, setCentres] = useState<CentreListItem[]>([]);
  const [centreOptionByTaxId, setCentreOptionByTaxId] = useState<Map<number, number | string>>(
    () => new Map()
  );
  const [centreScopeLabelFallback, setCentreScopeLabelFallback] = useState<string | null>(null);
  const [fees, setFees] = useState<Fee[]>([]);
  const [courses, setCourses] = useState<{ id: number; name: string }[]>([]);
  const [accommodations, setAccommodations] = useState<{ id: number; name: string }[]>([]);
  const [addons, setAddons] = useState<{ id: number; name: string }[]>([]);
  const [transfers, setTransfers] = useState<{ id: number; name: string }[]>([]);
  const [accommodationTypes, setAccommodationTypes] = useState<{ id: number; name: string }[]>([]);
  const [addOnTypes, setAddOnTypes] = useState<{ id: number; name: string }[]>([]);
  const [accommodationTypeByProductId, setAccommodationTypeByProductId] = useState<Map<number, number>>(
    () => new Map()
  );
  const [addOnTypeByProductId, setAddOnTypeByProductId] = useState<Map<number, number>>(() => new Map());

  const [loading, setLoading] = useState(true);
  const [taxCodeGlobalDup, setTaxCodeGlobalDup] = useState<Record<string, TaxCodeGlobalDupState>>({});
  const [taxCodeBlurredIds, setTaxCodeBlurredIds] = useState<Record<string, boolean>>({});
  const bandsRef = React.useRef(bands);
  bandsRef.current = bands;
  const taxCodeCheckNonce = React.useRef<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [sidebarCard, setSidebarCard] = useState<{
    rule: TaxBandRule;
    lane: 'include' | 'exclude';
    card: LaneCard;
  } | null>(null);

  const centreOptionsForResolve = useMemo(() => {
    return centres
      .filter(isCentreListItemActive)
      .map((c) => ({ value: centreOptionValue(c), label: c.name ?? String(c.id) }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [centres]);

  const centreDropdownOptions = useMemo(() => {
    const selectedNum = extractNumericCentreId(centreId);
    const list = centres.filter((c) => {
      if (isCentreListItemActive(c)) return true;
      if (selectedNum > 0) {
        const pid = positiveNumericCentreId(c);
        if (pid != null && pid === selectedNum) return true;
        const idNum = Number(String(c.id).trim());
        if (Number.isFinite(idNum) && Math.trunc(idNum) === selectedNum) return true;
      }
      return false;
    });
    let base: { value: number | string; label: string }[];
    if (!specificCentre || isCountrySelectionEmpty(countryId)) {
      base = list
        .map((c) => ({ value: centreOptionValue(c), label: c.name ?? String(c.id) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    } else {
      const { numericCountryId, isoCode } = centreFilterCountryKeys(countryId, countries);
      base = list
        .filter((c) => {
          if (numericCountryId > 0) {
            const cid = c.countryId != null ? Number(c.countryId) : undefined;
            return cid === numericCountryId;
          }
          if (isoCode && c.countryCode != null) {
            return String(c.countryCode).toUpperCase() === isoCode.toUpperCase();
          }
          return false;
        })
        .map((c) => ({ value: centreOptionValue(c), label: c.name ?? String(c.id) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    }
    return appendCentreSelectFallbackIfMissing(
      base,
      centreId,
      specificCentre,
      centreScopeLabelFallback
    );
  }, [centres, specificCentre, countryId, countries, centreId, centreScopeLabelFallback]);

  useEffect(() => {
    setTaxCodeBlurredIds({});
    setTaxCodeGlobalDup({});
  }, [editId, isAdd]);

  const taxCodeCheckKey = useMemo(
    () => bands.map((b) => `${b.id}:${normalizeTaxCodeDigits(b.taxCode)}`).join('|'),
    [bands]
  );

  useEffect(() => {
    if (loading) return;

    const timers: number[] = [];

    for (const band of bands) {
      const bandId = band.id;
      const digits = normalizeTaxCodeDigits(band.taxCode);

      if (digits.length < 4 || digits.length > 7) {
        setTaxCodeGlobalDup((p) => ({ ...p, [bandId]: 'idle' }));
        continue;
      }

      const dupSameForm = bands.some(
        (b) => b.id !== bandId && normalizeTaxCodeDigits(b.taxCode) === digits
      );
      if (dupSameForm) {
        setTaxCodeGlobalDup((p) => ({ ...p, [bandId]: 'idle' }));
        continue;
      }

      taxCodeCheckNonce.current[bandId] = (taxCodeCheckNonce.current[bandId] ?? 0) + 1;
      const nonce = taxCodeCheckNonce.current[bandId];

      setTaxCodeGlobalDup((p) => ({ ...p, [bandId]: 'pending' }));

      const tid = window.setTimeout(() => {
        const latest = bandsRef.current.find((b) => b.id === bandId);
        if (!latest) return;
        const dNow = normalizeTaxCodeDigits(latest.taxCode);
        if (dNow !== digits) return;
        if (taxCodeCheckNonce.current[bandId] !== nonce) return;

        const excludedBandId = parseApiBandIdFromRuleId(bandId);
        checkTaxCodeIsUnique(digits, excludedBandId)
          .then((unique) => {
            if (taxCodeCheckNonce.current[bandId] !== nonce) return;
            setTaxCodeGlobalDup((p) => ({ ...p, [bandId]: unique ? 'ok' : 'dup' }));
          })
          .catch(() => {
            if (taxCodeCheckNonce.current[bandId] !== nonce) return;
            setTaxCodeGlobalDup((p) => ({ ...p, [bandId]: 'error' }));
          });
      }, 450);
      timers.push(tid);
    }

    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [taxCodeCheckKey, loading]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [
          countriesData,
          centresData,
          productCatalog,
          accTypesData,
          addonTypesData,
          detailDto,
          centresDetailRaw,
        ] = await Promise.all([
          getAllCountries(),
          getCentres(),
          loadTaxManagerProductCatalog(),
          getAllAccommodationTypes(),
          getAllAddonTypes(),
          editId ? getTaxRuleById(editId) : Promise.resolve(null),
          getCentresInDetails().catch(() => null),
        ]);

        const activeFees = productCatalog.fees
          .filter((f) => f.active)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        setFees(activeFees);
        const centresListRaw = Array.isArray(centresData) ? centresData : [];
        const optionByTaxId = buildCentreOptionValueByAnyId(centresListRaw, centresDetailRaw);
        setCountries(countriesData ?? []);
        setCentres(centresListRaw);
        setCentreOptionByTaxId(optionByTaxId);

        const coursesRaw = productCatalog.courses as { id?: number; name?: string }[];
        const accRaw = productCatalog.accommodations as {
          id?: number;
          name?: string;
          typeId?: number;
        }[];
        const addonsRaw = productCatalog.addons as { id?: number; name?: string; typeId?: number }[];
        const transfersRaw = productCatalog.transfers as { id?: number; name?: string }[];

        const coursesOpts = normIdNameList(coursesRaw);
        const accOpts = normIdNameList(accRaw);
        const addonOpts = normIdNameList(addonsRaw);
        const transferOpts = normIdNameList(transfersRaw);

        setCourses(coursesOpts);
        setAccommodations(accOpts);
        setAddons(addonOpts);
        setAccommodationTypeByProductId(typeIdByProductMap(accRaw));
        setAddOnTypeByProductId(typeIdByProductMap(addonsRaw));
        setTransfers(transferOpts);
        setAccommodationTypes(normTypeList(accTypesData as { id?: number; name?: string }[]));
        setAddOnTypes(normTypeList(addonTypesData as { id?: number; name?: string }[]));

        if (isAdd) {
          setTaxName('');
          setSpecificCentre(false);
          setCountryId(null);
          setCentreId(null);
          setCentreScopeLabelFallback(null);
          setStartDate(null);
          setIncludeEndDate(false);
          setEndDate(null);
          setTaxIsActive(true);
          setBands([]);
          setRulesSectionVisible(false);
        } else if (detailDto && editId) {
          const coursesMap = new Map(coursesOpts.map((c) => [c.id, c.name]));
          const accMap = new Map(accOpts.map((a) => [a.id, a.name]));
          const addonsMap = new Map(addonOpts.map((a) => [a.id, a.name]));
          const transfersMap = new Map(transferOpts.map((t) => [t.id, t.name]));
          const accTypesMap = mapIdToName(accTypesData as { id?: number; name?: string }[]);
          const addOnTypesMap = mapIdToName(addonTypesData as { id?: number; name?: string }[]);
          const feesMap = new Map(activeFees.map((f) => [f.id, f.name]));
          const names: TaxProductNameLookup = {
            courses: coursesMap,
            accommodations: accMap,
            addons: addonsMap,
            transfers: transfersMap,
            accommodationTypes: accTypesMap,
            addOnTypes: addOnTypesMap,
            fees: feesMap,
          };
          const loaded = taxRuleDtoToLoadedState(detailDto, names);
          setTaxName(loaded.name);
          setSpecificCentre(loaded.specificCentre);
          const countriesList = (countriesData ?? []) as CountryWithNumericId[];
          if (loaded.specificCentre) {
            setCountryId(null);
            const centreLabelFromApi = extractCentreScopeLabelFromTaxRuleDto(detailDto)?.trim() ?? null;
            const taxNameTrim =
              detailDto.name != null && String(detailDto.name).trim() !== ''
                ? String(detailDto.name).trim()
                : null;
            setCentreScopeLabelFallback(centreLabelFromApi ?? taxNameTrim);
            setCentreId(
              loaded.centreId > 0
                ? matchCentreSelectValue(loaded.centreId, centresListRaw, optionByTaxId)
                : null
            );
            if (loaded.centreId > 0 && !centreLabelFromApi) {
              getCentreById(loaded.centreId)
                .then((c) => {
                  if (c?.name?.trim()) setCentreScopeLabelFallback(c.name.trim());
                })
                .catch(() => {});
            }
          } else {
            setCentreScopeLabelFallback(null);
            setCentreId(null);
            setCountryId(
              hasTaxCountryScopeSet(loaded.countryId)
                ? matchCountrySelectValue(loaded.countryId, countriesList)
                : null
            );
          }
          setStartDate(loaded.startDate);
          setIncludeEndDate(loaded.includeEndDate);
          setEndDate(loaded.endDate);
          setTaxIsActive(loaded.isActive);
          const accTypeMap = typeIdByProductMap(accRaw);
          const addonTypeMap = typeIdByProductMap(addonsRaw);
          const mirrorCtx = buildMirrorSyncContext(
            coursesOpts,
            accOpts,
            addonOpts,
            transferOpts,
            activeFees,
            accTypeMap,
            addonTypeMap
          );
          setBands(loaded.bands.map((b) => syncTaxBandMirrors(b, mirrorCtx)));
          setRulesSectionVisible(loaded.bands.length > 0);
        } else if (editId) {
          message.error('Tax rule not found. It may have been deleted.');
          navigate('/tax-manager');
        }
      } catch {
        message.error('Failed to load tax rule. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [location.pathname]);

  const productLists = useMemo(
    () => ({ courses, accommodations, addons, transfers, fees }),
    [courses, accommodations, addons, transfers, fees]
  );

  const getOptionsForProductType = useCallback(
    (pt: ProductTypeId) => optionsForProductType(pt, productLists),
    [productLists]
  );

  const mirrorContext = useMemo<MirrorSyncContext>(
    () => ({
      getOptions: (pt) => getOptionsForProductType(pt),
      accommodationTypeByProductId,
      addOnTypeByProductId,
    }),
    [getOptionsForProductType, accommodationTypeByProductId, addOnTypeByProductId]
  );

  const updateBand = useCallback((index: number, rule: TaxBandRule) => {
    setBands((prev) => {
      const next = [...prev];
      next[index] = rule;
      return next;
    });
  }, []);

  const removeBand = useCallback((index: number) => {
    setSidebarCard((prev) => {
      if (!prev) return prev;
      const current = bands[index];
      if (current && prev.rule.id === current.id) return null;
      return prev;
    });
    setBands((prev) => prev.filter((_, i) => i !== index));
    setRulesSectionVisible((prevVisible) => {
      if (!prevVisible) return prevVisible;
      const nextLen = bands.length - 1;
      return nextLen > 0;
    });
  }, [bands]);

  const removeCard = useCallback(
    (ruleIndex: number, lane: 'include' | 'exclude', cardId: string) => {
      Modal.confirm({
        title: 'Delete card',
        content: 'Are you sure you want to delete?',
        okText: 'Yes',
        cancelText: 'Cancel',
        onOk: () => {
          setBands((prev) => {
            const r = prev[ruleIndex];
            if (!r) return prev;
            const next = [...prev];
            const filtered =
              lane === 'include'
                ? r.include.filter((c) => c.id !== cardId)
                : r.exclude.filter((c) => c.id !== cardId);
            const updated =
              lane === 'include' ? { ...r, include: filtered } : { ...r, exclude: filtered };
            next[ruleIndex] = syncTaxBandMirrors(updated, mirrorContext);
            return next;
          });
        },
      });
    },
    [mirrorContext]
  );

  const updateCardCondition = useCallback(
    (
      ruleIndex: number,
      lane: 'include' | 'exclude',
      cardId: string,
      condition: ProductTypeCondition | AccommodationCondition
    ) => {
      setBands((prev) => {
        const r = prev[ruleIndex];
        if (!r) return prev;
        const cards = lane === 'include' ? r.include : r.exclude;
        const card = cards.find((c) => c.id === cardId);
        if (!card) return prev;
        const summary = getSummary(condition);
        const updatedCard = { ...card, condition, summary };
        const next = [...prev];
        const updatedRule =
          lane === 'include'
            ? { ...r, include: cards.map((c) => (c.id === cardId ? updatedCard : c)) }
            : { ...r, exclude: cards.map((c) => (c.id === cardId ? updatedCard : c)) };
        next[ruleIndex] = syncTaxBandMirrors(updatedRule, mirrorContext);
        return next;
      });
      setSidebarCard(null);
    },
    [mirrorContext]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const source = active.data.current;
      const target = over.data.current;
      if (!source?.productType || source.source !== 'field-library' || !target?.lane || !target?.ruleId) return;

      const productType = source.productType as ProductTypeId;
      const lane = target.lane as 'include' | 'exclude';
      const ruleIndex = bands.findIndex((r) => r.id === target.ruleId);
      if (ruleIndex < 0) return;

      const rule = bands[ruleIndex];
      const otherLane = lane === 'include' ? rule.exclude : rule.include;
      const sameLane = lane === 'include' ? rule.include : rule.exclude;

      if (sameLane.some((c) => c.productType === productType)) {
        message.warning(
          `You already added "${productType}" to ${lane === 'include' ? 'Include' : 'Exclude'} for this rule.`
        );
        return;
      }
      if (otherLane.some((c) => c.productType === productType)) {
        message.warning(
          `You can't add "${productType}" to both Include and Exclude for the same rule`
        );
        return;
      }

      const newCard: LaneCard = {
        id: generateCardId(),
        productType,
        summary: 'Specify product',
      };

      const ruleWithCard =
        lane === 'include'
          ? { ...rule, include: [...rule.include, newCard] }
          : { ...rule, exclude: [...rule.exclude, newCard] };

      setBands((prev) => {
        const next = [...prev];
        next[ruleIndex] = ruleWithCard;
        return next;
      });

      setSidebarCard({ rule: ruleWithCard, lane, card: newCard });
    },
    [bands]
  );

  const resolvedLocationIds = useMemo(() => {
    if (specificCentre) {
      const cenId = extractNumericCentreId(centreId);
      return { applyToSpecificCentre: true as const, countryId: 0, centreId: cenId };
    }
    return {
      applyToSpecificCentre: false as const,
      countryId: resolveTaxRuleCountryForPayload(countryId, countries),
      centreId: 0,
    };
  }, [specificCentre, countryId, centreId, countries]);

  const headerValid =
    taxName.trim().length > 0 &&
    startDate != null &&
    startDate.isValid() &&
    (!includeEndDate ||
      endDate == null ||
      (endDate.isValid() && !endDate.isBefore(startDate, 'day'))) &&
    (!specificCentre ? !isCountrySelectionEmpty(countryId) : hasCentreSelectionForHeader(centreId));

  const bandsValid =
    rulesSectionVisible &&
    bands.length > 0 &&
    bands.every((b) => {
      const raw = b.taxRatePercent;
      const rate = typeof raw === 'number' ? raw : Number(raw);
      const rateOk = Number.isFinite(rate) && rate >= 0 && rate <= 100;
      const nameOk = b.name.trim().length > 0;
      return nameOk && isTaxCodeValidForSave(b, bands, taxCodeGlobalDup) && rateOk;
    });

  const canSave = headerValid && bandsValid;

  const handleSave = useCallback(async () => {
    if (rulesSectionVisible && bands.length === 0) {
      message.error('Tax must contain at least one rule.');
      return;
    }
    if (bands.some((b) => !b.name.trim())) {
      message.error('Rule name is required');
      return;
    }
    if (!headerValid || !bandsValid || !startDate) return;
    if (includeEndDate) {
      if (endDate == null || !endDate.isValid()) {
        message.error('Please select an end date, or turn off "Include End Date".');
        return;
      }
      if (endDate.isBefore(startDate, 'day')) {
        message.error('End Date cannot be earlier than Start Date.');
        return;
      }
    }
    const incompleteRule = bands.find(hasIncompleteProductSelection);
    if (incompleteRule) {
      message.error(
        'One or more product cards need a complete selection (choose products or types, or remove incomplete cards).'
      );
      return;
    }
    const centreIdForPayload = specificCentre ? taxCentreIdForSavePayload(centreId, centreOptionByTaxId) : 0;
    if (specificCentre && centreIdForPayload <= 0) {
      message.error('Select a centre, or turn off “Apply to specific centre”.');
      return;
    }
    setSaving(true);
    try {
      const payload = buildTaxRulePutPayload({
        name: taxName.trim(),
        applyToSpecificCentre: specificCentre,
        countryId: resolvedLocationIds.countryId,
        centreId: centreIdForPayload,
        startDate,
        endDate: includeEndDate && endDate && endDate.isValid() ? endDate : null,
        isActive: taxIsActive,
        bands,
      });
      const putId = editId && editId > 0 ? editId : 0;
      const res = await saveTaxRule(putId, payload);
      if (res.ok) {
        message.success(editId ? 'Tax rule updated successfully.' : 'Tax rule saved successfully.');
        navigate('/tax-manager');
      } else {
        message.error(formatTaxSaveErrorMessage(res.message) || 'Failed to save tax rule.');
      }
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'Failed to save tax rule.';
      message.error(formatTaxSaveErrorMessage(raw) || raw);
    } finally {
      setSaving(false);
    }
  }, [
    headerValid,
    bandsValid,
    taxName,
    specificCentre,
    centreId,
    centreOptionByTaxId,
    resolvedLocationIds,
    startDate,
    endDate,
    includeEndDate,
    taxIsActive,
    bands,
    editId,
    navigate,
  ]);

  const handleAddRuleClick = () => {
    setRulesSectionVisible(true);
    setBands((prev) => (prev.length > 0 ? prev : [newBandFromTaxName(taxName.trim())]));
  };

  const handleAddAnotherBand = () => {
    setBands((prev) => [...prev, newBandFromTaxName(taxName.trim())]);
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const countrySelectOptions = useMemo(
    () =>
      countries
        .filter((c) => c.name)
        .map((c) => ({ value: countryOptionValue(c), label: String(c.name) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [countries]
  );

  const saveButton = (
    <Button type="primary" onClick={handleSave} disabled={!canSave} loading={saving}>
      Save
    </Button>
  );

  if (loading) {
    return (
      <div className="commission-loading">
        <Spin size="large" />
        <span className="commission-loading-text">Loading...</span>
      </div>
    );
  }

  return (
    <div className="commission-builder-page tax-add-page">
      <div className="commission-back-link-wrap">
        <Link to="/tax-manager" className="commission-back-link">
          <LeftOutlined />
          Back to Taxes
        </Link>
      </div>
      <Title level={4}>{isAdd ? 'Add Tax' : 'Edit Tax'}</Title>

      <div className="tax-header-card">
        <div className="tax-header-top">
          <Space wrap size="middle" className="tax-header-fields">
            <div className="tax-field-group">
              <div className="tax-field-label tax-required-label">Tax Name</div>
              <Input
                placeholder="Tax name"
                value={taxName}
                onChange={(e) => setTaxName(e.target.value)}
                style={{ width: 260 }}
              />
            </div>
            {!specificCentre ? (
              <div className="tax-field-group">
                <div className="tax-field-label tax-required-label">applies to</div>
                <Select
                  key="country-select"
                  placeholder="Select country"
                  allowClear
                  showSearch={{
                    optionFilterProp: 'label',
                    filterOption: (input, option) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.trim().toLowerCase()),
                  }}
                  style={{ width: 220 }}
                  options={countrySelectOptions}
                  value={countryId ?? undefined}
                  onChange={(v) => setCountryId(v ?? null)}
                />
              </div>
            ) : (
              <div className="tax-field-group">
                <div className="tax-field-label tax-required-label">applies to</div>
                <Select
                  key="centre-select"
                  placeholder="Select centre"
                  allowClear
                  showSearch={{
                    optionFilterProp: 'label',
                    filterOption: (input, option) =>
                      String(option?.label ?? '')
                        .toLowerCase()
                        .includes(input.trim().toLowerCase()),
                  }}
                  style={{ width: 220 }}
                  options={centreDropdownOptions}
                  value={centreId ?? undefined}
                  onChange={(v) => {
                    if (v == null || v === '') {
                      setCentreId(null);
                      return;
                    }
                    if (typeof v === 'number' && v > 0) {
                      setCentreId(v);
                      return;
                    }
                    const n = Number(String(v).trim());
                    setCentreId(Number.isFinite(n) && n > 0 ? n : v);
                  }}
                />
              </div>
            )}
            <div className="tax-field-group tax-field-group--checkbox-row">
              <Checkbox
                checked={specificCentre}
                onChange={(e) => {
                  const on = e.target.checked;
                  setSpecificCentre(on);
                  if (on) {
                    setCountryId(null);
                  } else {
                    setCentreId(null);
                    setCentreScopeLabelFallback(null);
                  }
                }}
              >
                Apply to specific centre
              </Checkbox>
            </div>
            <div className="tax-field-group">
              <div className="tax-field-label tax-required-label">from</div>
              <DatePicker
                format={DateHelper.DATE_PICKER_FORMATS}
                allowClear
                value={startDate}
                onChange={(d) => setStartDate(d)}
                style={{ width: 180 }}
                placeholder="Select Date"
              />
            </div>
            <div className="tax-field-group tax-field-group--checkbox-row">
              <Checkbox
                checked={includeEndDate}
                onChange={(e) => {
                  setIncludeEndDate(e.target.checked);
                  if (!e.target.checked) setEndDate(null);
                }}
              >
                Include End Date
              </Checkbox>
            </div>
            <div
              className={`tax-field-group${includeEndDate ? '' : ' tax-field-group--checkbox-row'}`}
            >
              {includeEndDate && <div className="tax-field-label">to</div>}
              <Space align="center" wrap size="middle">
                {includeEndDate && (
                  <DatePicker
                    format={DateHelper.DATE_PICKER_FORMATS}
                    allowClear
                    value={endDate}
                    onChange={(d) => setEndDate(d)}
                    style={{ width: 180 }}
                    placeholder="Select Date"
                  />
                )}
                <Switch
                  checked={taxIsActive}
                  onChange={setTaxIsActive}
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                />
              </Space>
            </div>
          </Space>
        </div>
      </div>

      {!rulesSectionVisible && (
        <div className="tax-add-rule-stub">
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddRuleClick}>
            Add rule
          </Button>
        </div>
      )}

      {rulesSectionVisible && (
        <>
          <div className="commission-builder-layout tax-builder-layout">
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <div className="commission-builder-main">
                {bands.map((rule, idx) => (
                  <TaxRuleBlock
                    key={rule.id}
                    rule={rule}
                    taxCodeError={mergeTaxCodeInlineError(
                      computeTaxCodeFieldError({
                        raw: rule.taxCode,
                        blurred: taxCodeBlurredIds[rule.id] ?? false,
                        bandId: rule.id,
                        allBands: bands,
                      }),
                      taxCodeGlobalDup[rule.id]
                    )}
                    onTaxCodeBlur={() =>
                      setTaxCodeBlurredIds((prev) => ({ ...prev, [rule.id]: true }))
                    }
                    onRuleChange={(r) => updateBand(idx, r)}
                    onRemoveRule={() => removeBand(idx)}
                    onCardRemove={(lane, cardId) => removeCard(idx, lane, cardId)}
                    onCardConfigure={(lane, card) => setSidebarCard({ rule, lane, card })}
                  />
                ))}
              </div>
              {!sidebarCard && (
                <div className="commission-builder-sidebar">
                  <FieldLibrary />
                </div>
              )}
            </DndContext>
            {sidebarCard && !sidebarCard.card.isMirror && (
              <div className="commission-builder-sidebar">
                <ParameterSidebar
                  key={sidebarCard.card.id}
                  productType={sidebarCard.card.productType}
                  card={sidebarCard.card}
                  options={getOptionsForProductType(sidebarCard.card.productType)}
                  accommodationTypes={
                    sidebarCard.card.productType === 'Accommodation' ? accommodationTypes : undefined
                  }
                  addOnTypes={sidebarCard.card.productType === 'AddOn' ? addOnTypes : undefined}
                  onSave={(cond) => {
                    const ri = bands.findIndex((r) => r.id === sidebarCard.rule.id);
                    if (ri >= 0) updateCardCondition(ri, sidebarCard.lane, sidebarCard.card.id, cond);
                  }}
                  onClear={() => {
                    const ri = bands.findIndex((r) => r.id === sidebarCard.rule.id);
                    if (ri >= 0)
                      updateCardCondition(ri, sidebarCard.lane, sidebarCard.card.id, {
                        mode: 'ANY',
                        selectedIds: [],
                      });
                  }}
                  onClose={() => setSidebarCard(null)}
                />
              </div>
            )}
          </div>
          <div className="tax-add-another-rule">
            <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddAnotherBand}>
              Add rule
            </Button>
          </div>
        </>
      )}

      <div className="commission-save-row">
        <Space>
          <Button onClick={() => navigate('/tax-manager')}>Cancel</Button>
          {saveButton}
        </Space>
      </div>
    </div>
  );
};